<template>
  <div data-component="apps-integration-options">
    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="!options.avatar"
      class="app-avatar-default"
      :style="getAvatarStyle"
      v-html="DEFAULT_AVATAR_SVG"
    />
    <!-- eslint-enable vue/no-v-html -->

    <img
      v-if="options.avatar"
      class="app-avatar"
      :src="options.avatar"
    >

    <or-input
      v-model="$v.options.avatar.$model"
      :label="$t('HITL.common.properties.avatar')"
      :placeholder="$t('HITL.app-launcher.apps-integration.avatar-option-placeholder')"
    />

    <or-input
      v-model="$v.options.color.$model"
      :label="$t('HITL.app-launcher.apps-integration.color-option-label')"
      :placeholder="DEFAULT_COLOR_PLACEHOLDER"
      :hint="$t('HITL.app-launcher.apps-integration.color-option-hint')"
      :error="getErrorText('color')"
      required
    />

    <or-input
      v-model="$v.options.name.$model"
      :label="$t('HITL.common.properties.name')"
      :error="getErrorText('name')"
      maxlength="255"
      required
    />

    <or-textarea
      v-model="$v.options.description.$model"
      :label="$t('HITL.common.properties.description')"
      :rows="{ min: 3, max: 3 }"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, getCurrentInstance, PropType } from 'vue-demi';
import { useVuelidate } from '@vuelidate/core';
import { required, url, helpers } from '@vuelidate/validators';
import {
  OrInputV3 as OrInput,
  OrTextareaV3 as OrTextarea,
} from '@onereach/ui-components-25';

import { OneReachApp } from '../../../types';
import { DEFAULT_AVATAR_SVG } from '../constants';

export default defineComponent({
  name: 'AppsIntegrationOptions',
  components: { OrInput, OrTextarea },
  props: {
    app: {
      type: Object as PropType<OneReachApp>,
      default: () => ({}),
    },
    account: {
      type: String,
      default: '',
    },
    env: {
      type: String,
      default: '',
    },
  },
  emits: ['app-change'],
  setup() {
    const vm = getCurrentInstance()!;

    const message = vm.proxy.$t('HITL.common.errors.property-required');
    const cRequired = helpers.withMessage(message as string, required);

    return { $v: useVuelidate(), cRequired };
  },
  data() {
    const name = this.$t('HITL.common.app-title');
    const description = `${name} - [${this.account}] - [${this.env}]`;
    const DEFAULT_COLOR = '42C3F8';

    return {
      options: {
        avatar: this.app.avatar,
        color: this.app.color || DEFAULT_COLOR,
        name: this.app.name || name,
        description: this.app.description || description,
      },
      DEFAULT_COLOR: 'var(--or-c-on-background)',
      DEFAULT_COLOR_PLACEHOLDER: DEFAULT_COLOR,
      DEFAULT_AVATAR_SVG,
    };
  },
  validations() {
    return {
      options: {
        avatar: { url },
        color: { required: this.cRequired },
        name: { required: this.cRequired },
        description: {},
      },
    };
  },
  computed: {
    getAvatarStyle() {
      const color = this.options.color || this.DEFAULT_COLOR;
      return { fill: `#${color}` };
    },
  },
  methods: {
    getErrorText(property: string) {
      const errors = this.$v.options[property].$errors;
      return errors.length ? errors[0].$message : '';
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='apps-integration-options'] {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);

  .app-avatar-default {
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--or-c-surface-1);
    border-radius: 50%;
  }

  .app-avatar,
  .app-avatar-default {
    height: 5rem;
    width: 5rem;

    ::v-deep svg {
      height: 85%;
      width: 85%;
    }
  }
}
</style>
